import { get } from 'lodash';

/**
 * Get event title
 * @param {import('@sentry/types').ErrorEvent} event - event
 * @returns {string} - event title
 */
export const getEventTitle = (event) => {
  if (event.exception) {
    return get(event.exception, 'values[0].value', '');
  }
  return event.message || '';
};

export class SentryTestException extends Error {
  constructor(message = '') {
    super(message);
    this.name = 'SentryTestException';
    this.message = message;
  }
}
export const isSentryTestException = (error) => error?.type === 'SentryTestException';

/**
 * Get exceptions from event
 * @param {import('@sentry/types').ErrorEvent} event - event
 * @returns {import('@sentry/types').Exception[]} - exceptions
 */
export const exceptionsFromEvent = (event) => event?.exception?.values || [];

/**
 * Get frames from exception
 * @param {import('@sentry/types').Exception} exception
 * @returns {import('@sentry/types').StackFrame[]}
 */
export const framesFromException = (exception) => exception?.stacktrace?.frames || [];

/**
 * Is error message present in the path of the event
 * @param {import('@sentry/types').ErrorEvent} event - event
 * @param {string} value - error message
 * @returns {boolean} - is error message present in the path of the event
 */
export const exceptionIncludesValue = (event, value) =>
  exceptionsFromEvent(event).some((ex) => ex.value != null && ex.value.includes(value));

/**
 * Is error present in the path of the event
 * @param {import('@sentry/types').ErrorEvent} event - event
 * @param {{type?: string, value?: string, path?: string}} error - error
 * @returns {boolean} - is error present in the path of the event
 */
export const eventHasError = (event, error) => {
  const filterException = (exception) => {
    if (error.type !== undefined && exception.type !== error.type) {
      return false;
    }

    if (error.value !== undefined && !`${exception.value}`.includes(error.value)) {
      return false;
    }

    if (error.path !== undefined) {
      const stackFrames = framesFromException(exception);
      return stackFrames.some((frame) => `${frame.filename}`.includes(error.path));
    }

    return true;
  };

  return exceptionsFromEvent(event).some(filterException);
};

/**
 * In some cases we have "sentry" wrapper module in the stacktrace - we don't need it
 * @param {import('@sentry/types').StackFrame[]} frames - incoming frames
 * @returns {import('@sentry/types').StackFrame[]} - cleaned frames
 */
export const withoutFirstSentryFrame = (frames) => {
  if (
    frames[0]?.function === 'sentryWrapped' ||
    frames[0]?.filename?.includes('node_modules/@sentry')
  ) {
    return frames.slice(1);
  }
  return frames;
};

/**
 * Check if frame filename includes a string
 * NOTE: Although we can see "abs_path" property in event data in Sentry app, in runtime it's not always available,
 *       so we should check both "filename" and "abs_path"
 * @param {import('@sentry/types').StackFrame} frame - frame
 * @param {string} str - string to search
 * @returns {boolean} - is string present in the frame "filename" or "abs_path"
 */
export const frameFilenameIncludes = (frame, str) => {
  const filename = frame?.filename;
  const absPath = frame?.abs_path;
  return Boolean(filename?.includes(str) || absPath?.includes(str));
};
